import React, { createContext, useContext, useState, ReactNode } from 'react';
import { en } from '../translations/en';
import { fr } from '../translations/fr';

type Language = 'en' | 'fr';

interface TranslationContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, any>) => string;
  currentTranslations: typeof en;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

interface TranslationProviderProps {
  children: ReactNode;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  // Get language from localStorage or default to French
  const getInitialLanguage = (): Language => {
    const savedLanguage = localStorage.getItem('app-language');
    return (savedLanguage as Language) || 'fr';
  };

  const [language, setLanguage] = useState<Language>(getInitialLanguage);

  const translations = {
    en,
    fr
  };

  const currentTranslations = translations[language];

  // Helper function to get nested translation values
  const t = (key: string, params?: Record<string, any>): string => {
    const keys = key.split('.');
    let value: any = currentTranslations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if translation not found
        let fallbackValue: any = en;
        for (const fallbackKey of keys) {
          if (fallbackValue && typeof fallbackValue === 'object' && fallbackKey in fallbackValue) {
            fallbackValue = fallbackValue[fallbackKey];
          } else {
            return key; // Return the key if not found in either language
          }
        }
        return fallbackValue;
      }
    }

    let result = typeof value === 'string' ? value : key;
    for (const param in params) {
      result = result.replace(`{${param}}`, params[param]);
    }
    return result;
  };

  const value: TranslationContextType = {
    language,
    setLanguage: (lang: Language) => {
      setLanguage(lang);
      localStorage.setItem('app-language', lang);
    },
    t,
    currentTranslations
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};
